import { setValueGenericWay } from '@/common/helpers/reflectionUtils'
import { fetchCaseActions } from '@/common/services/CaseActionService'
import { getBulkInteractions } from '@/common/services/InteractionService'
import skillServerData from '@/common/services/serverData/skillServerData'
import teamServerData from '@/common/services/serverData/teamServerData'
import userServerData from '@/common/services/serverData/userServerData'
import type { CaseDetailsDTOV1, Case, CaseDTOV3 } from '@/common/types/case'
import { type CaseActionDTOV1 } from '@/common/types/caseAction'
import { EntityFieldCodeEnum } from '@/common/types/fields'
import { type FetchCallbackFunctions } from '@/common/types/general'
import { type InteractionDetailsDTOV1 } from '@/common/types/interaction'
import { type SkillDTOV2 } from '@/common/types/skills'
import { type TeamDTOV2 } from '@/common/types/teams'
import { type UserDTOV3 } from '@/common/types/users'
import keyBy from 'lodash/keyBy'

export type keyOfCase = keyof Case
export type keyOfCaseDetails = keyof CaseDetailsDTOV1 | keyof CaseDTOV3

/**
 * Case Data properties who matches with action data properties
 */
export type keyOfCaseActionData = keyof CaseActionDTOV1
export const CorrespondingCaseDetailsToActionDataMap = new Map<EntityFieldCodeEnum, [keyOfCaseDetails, keyOfCaseActionData]>([
  [EntityFieldCodeEnum.LAST_ACTION_TYPE, ['lastActionId', 'type']],
  [EntityFieldCodeEnum.LAST_ACTION_REASON, ['lastActionId', 'reasonCode']],
  [EntityFieldCodeEnum.LAST_EVENT_DATE, ['lastActionId', 'date']],
  [EntityFieldCodeEnum.LAST_ACTION_TYPE_REASON_NOT_EMPTY, ['lastActionIdWithReason', 'type']],
  [EntityFieldCodeEnum.LAST_ACTION_REASON_NOT_EMPTY, ['lastActionIdWithReason', 'reasonCode']],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_COMMENT, ['lastValidationActionId', 'comment']],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_DATE, ['lastValidationActionId', 'date']],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_TYPE_NAME, ['lastValidationActionId', 'type']],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_USER, ['lastValidationActionId', 'user']],
])

/**
 * Case Data properties who matches with interaction data properties
 */
export type keyOfInteractionData = keyof InteractionDetailsDTOV1
export const CorrespondingCaseDetailsToInteractionDataMap = new Map<EntityFieldCodeEnum, [keyOfCaseDetails, keyOfInteractionData]>([
  [EntityFieldCodeEnum.FIRST_OUTBOUND_STATUS, ['firstOutboundInteractionId', 'statusCode']],
  [EntityFieldCodeEnum.FIRST_OUTBOUND_TYPE, ['firstOutboundInteractionId', 'typeCode']],
  [EntityFieldCodeEnum.FIRST_OUTBOUND_DATE, ['firstOutboundInteractionId', 'interactionDate']],
  [EntityFieldCodeEnum.FIRST_INBOUND_STATUS, ['firstInboundInteractionId', 'statusCode']],
  [EntityFieldCodeEnum.FIRST_INBOUND_TYPE, ['firstInboundInteractionId', 'typeCode']],
  [EntityFieldCodeEnum.FIRST_INBOUND_DATE, ['firstInboundInteractionId', 'interactionDate']],
])

/**
 * user properties from case properties who match with user data properties
 */
export type keyOfUserSummary = keyof UserDTOV3
export const CorrespondingUserDataCaseToUserSummaryMap = new Map<EntityFieldCodeEnum, [keyOfCaseDetails, keyOfUserSummary]>([
  [EntityFieldCodeEnum.ASSIGNEE_EXTERNAL_ID, ['assignee', 'externalId']],
  [EntityFieldCodeEnum.ASSIGNEE_PHONE, ['assignee', 'phone']],
  [EntityFieldCodeEnum.ASSIGNEE_USERGROUP_NAME, ['assignee', 'teamCode']],
  [EntityFieldCodeEnum.CREATION_USER_EMAIL, ['creator', 'emailAddress']],
])

/**
 * Case Data properties who matches with team properties
 */
export type keyOfTeam = keyof TeamDTOV2
export const CorrespondingTeamDataCaseToTeamMap = new Map<EntityFieldCodeEnum, [keyOfCaseDetails, keyOfTeam]>([
  [EntityFieldCodeEnum.ASSIGNEE_SITE, ['assignee', 'level3']],
  [EntityFieldCodeEnum.CREATION_USER_SITE, ['creator', 'level3']],
])

export const getInformationToFetchCasesFields = (
  cases: CaseDetailsDTOV1[],
  keysActionsDataToFetch: Set<keyOfCaseDetails>,
  keysInteractionsDataToFetch: Set<keyOfCaseDetails>,
) => {
  return cases.reduce<{
    customerCases: Map<number, Case[]>
    actionDataCases: Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>
    interactionDataCases: Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>
    caseUserData: Map<Case, { assignee?: string; creator?: string }>
  }>(
    (accumulator, caseDetails) => {
      if (caseDetails.customerId) {
        if (accumulator.customerCases.has(caseDetails.customerId)) accumulator.customerCases.get(caseDetails.customerId)?.push(caseDetails)
        else accumulator.customerCases.set(caseDetails.customerId, [caseDetails])
      }

      for (const key of keysActionsDataToFetch) {
        setNewKeyOnMap(accumulator.actionDataCases, caseDetails, key)
      }

      for (const key of keysInteractionsDataToFetch) {
        setNewKeyOnMap(accumulator.interactionDataCases, caseDetails, key)
      }

      accumulator.caseUserData.set(caseDetails, { assignee: caseDetails.assignee, creator: caseDetails.creator })

      return accumulator
    },
    {
      customerCases: new Map<number, Case[]>(),
      actionDataCases: new Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>(),
      interactionDataCases: new Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>(),
      caseUserData: new Map<Case, { assignee?: string; creator?: string }>(),
    },
  )
}

const setNewKeyOnMap = (map: Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>, caseDetails: CaseDetailsDTOV1, key: keyOfCaseDetails) => {
  if (caseDetails[key]) {
    const mapKey: number = caseDetails[key] as number
    const data = map.get(mapKey)
    const keys = data ? [...data.keys, key] : [key]
    map.set(mapKey, { oneCase: caseDetails, keys })
  }
}

export const fetchCasesActionsData = async (
  actionDataCases: Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>,
  propertyActionsDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  const caseActionIds = new Set<number>(actionDataCases.keys())

  if (!caseActionIds.size) {
    onSuccess?.()
    return
  }

  try {
    const caseActions: {
      [id: number]: CaseActionDTOV1
    } = keyBy(await fetchCaseActions([...caseActionIds]), (a) => a.id)

    actionDataCases.forEach(({ oneCase, keys }, caseActionId) => {
      const caseAction = caseActions[caseActionId]

      if (!caseAction) return

      propertyActionsDataToSetOnCase.forEach((property: keyOfCase, fieldCode: EntityFieldCodeEnum) => {
        const [caseProperty, actionProperty] = CorrespondingCaseDetailsToActionDataMap.get(fieldCode)!

        keys.forEach((key: keyOfCaseDetails) => {
          if (key !== caseProperty) return

          const value = caseAction[actionProperty]
          if (typeof value !== 'undefined') {
            setValueGenericWay(oneCase, property, value)
          }
        })
      })
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

export const fetchCasesInteractionsData = async (
  interactionDataCases: Map<number, { oneCase: Case; keys: keyOfCaseDetails[] }>,
  propertyInteractionsDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  const interactionsIds = new Set<number>(interactionDataCases.keys())

  if (!interactionsIds.size) {
    onSuccess?.()
    return
  }

  try {
    const interactions: {
      [id: number]: InteractionDetailsDTOV1
    } = keyBy(await getBulkInteractions([...interactionsIds]), (a) => a.id)

    interactionDataCases.forEach(({ oneCase, keys }, interactionId) => {
      const interaction = interactions[interactionId]

      if (!interaction) return

      propertyInteractionsDataToSetOnCase.forEach((property: keyOfCase, fieldCode: EntityFieldCodeEnum) => {
        const [caseProperty, interactionProperty] = CorrespondingCaseDetailsToInteractionDataMap.get(fieldCode)!

        keys.forEach((key: keyOfCaseDetails) => {
          if (key !== caseProperty) return

          const value = interaction[interactionProperty]
          if (typeof value !== 'undefined') {
            setValueGenericWay(oneCase, property, value)
          }
        })
      })
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

export const fetchCasesUsersData = async (
  caseUserData: Map<Case, { assignee?: string; creator?: string }>,
  propertyUserDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  propertyTeamDataToSetOnCase: Map<EntityFieldCodeEnum, keyOfCase>,
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  const isTeamDataExpected = propertyTeamDataToSetOnCase.size > 0
  const isUserDataExpected = propertyUserDataToSetOnCase.size > 0
  const extraUserOrTeamDataExpected = caseUserData.size > 0 && (isUserDataExpected || isTeamDataExpected)

  if (!extraUserOrTeamDataExpected) {
    onSuccess?.()
    return
  }

  try {
    const promises: Promise<UserDTOV3[] | (TeamDTOV2[] | undefined)>[] = [userServerData.fetch()]

    if (isTeamDataExpected) {
      promises.push(teamServerData.fetch())
    }
    const [usersRaw, teamsRaw] = (await Promise.all(promises)) as [UserDTOV3[], TeamDTOV2[] | undefined]

    const users: {
      [login: string]: UserDTOV3
    } = keyBy(usersRaw, (a) => a.login)

    const teams: {
      [code: UUID]: TeamDTOV2
    } = keyBy(teamsRaw, (a) => a.code)

    caseUserData.forEach(({ assignee, creator }, oneCase) => {
      const assigneeUser = assignee ? users[assignee] : undefined
      const creatorUser = creator ? users[creator] : undefined
      if (isUserDataExpected) {
        propertyUserDataToSetOnCase.forEach((property: keyOfCase, fieldCode: EntityFieldCodeEnum) => {
          const [caseProperty] = CorrespondingUserDataCaseToUserSummaryMap.get(fieldCode)!
          if ('assignee' === caseProperty && assigneeUser) {
            setUserFieldOnCase(assigneeUser, fieldCode, oneCase, property)
          } else if ('creator' === caseProperty && creatorUser) {
            setUserFieldOnCase(creatorUser, fieldCode, oneCase, property)
          }
        })
      }
      if (isTeamDataExpected) {
        propertyTeamDataToSetOnCase.forEach((property: keyOfCase, fieldCode: EntityFieldCodeEnum) => {
          const [caseProperty] = CorrespondingTeamDataCaseToTeamMap.get(fieldCode)!
          if ('assignee' === caseProperty && assigneeUser) {
            setTeamFieldOnCase(teams, assigneeUser, fieldCode, oneCase, property)
          } else if ('creator' === caseProperty && creatorUser) {
            setTeamFieldOnCase(teams, creatorUser, fieldCode, oneCase, property)
          }
        })
      }
    })
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}

const setUserFieldOnCase = (user: UserDTOV3, fieldCode: EntityFieldCodeEnum, oneCase: Case, property: keyOfCase) => {
  const userProperty = CorrespondingUserDataCaseToUserSummaryMap.get(fieldCode)![1]
  const value = user[userProperty]
  if (typeof value !== 'undefined') {
    setValueGenericWay(oneCase, property, value)
  }
}

const setTeamFieldOnCase = (
  teams: {
    [code: UUID]: TeamDTOV2
  },
  user: UserDTOV3,
  fieldCode: EntityFieldCodeEnum,
  oneCase: Case,
  property: keyOfCase,
) => {
  const team = teams[user.teamCode]
  const teamProperty = CorrespondingTeamDataCaseToTeamMap.get(fieldCode)![1]
  const value = team[teamProperty]

  if (typeof value !== 'undefined') {
    setValueGenericWay(oneCase, property, value)
  }
}

export const fetchCasesSkillsData = async (
  hasSkillTargetProcessTimeField: boolean,
  cases: Case[],
  { onSuccess, onFailure }: FetchCallbackFunctions = {},
) => {
  if (!hasSkillTargetProcessTimeField) {
    onSuccess?.()
    return
  }

  try {
    const skillsRaw = await skillServerData.fetch()

    if (typeof skillsRaw === 'undefined') {
      throw new Error('error fetching skills')
    }

    const skills: {
      [code: string]: SkillDTOV2
    } = keyBy(skillsRaw, (a) => a.code)

    for (const oneCase of cases) {
      oneCase.skillTargetProcessingTime = skills[oneCase.currentSkillCode].targetProcessingTime
    }
    onSuccess?.()
  } catch (error) {
    onFailure?.()
    console.error(error)
  }
}
